import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Search, Minus, Plus, Wrench, LayoutGrid, Rows3, Table2, Eye } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, todayStr, shiftDateStr } from '../lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HapusCari } from '../components/HapusCari';

const CATEGORIES = ['', 'Kebaya', 'Baju Bodo', 'Bawahan', 'Dress', 'Aksesoris', 'Jas Tutup', 'Kemeja / Batik Pria', 'Seserahan', 'Ring Box'];

const VIEWS = [
  { v: 'compact', l: 'List', icon: Rows3 },
  { v: 'grid', l: 'Gambar', icon: LayoutGrid },
  { v: 'table', l: 'Tabel', icon: Table2 },
];

const STATE_L = { sewa: 'disewa', booking: 'dibooking', laundry: 'di laundry' };

function InspectDialog({ product, onClose }) {
  const [d, setD] = useState(todayStr());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!product) return;
    setData(null);
    api.get(`/stock/inspect?product_id=${product.id}&date=${d}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [product, d]);

  return (
    <Dialog open={!!product} onOpenChange={onClose}>
      <DialogContent data-testid="stock-inspect-dialog" className="bg-ivory max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brown-dark">{product?.code} — {product?.name}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Cek tanggal</label>
          <input data-testid="inspect-date" type="date" value={d} onChange={(e) => setD(e.target.value)}
            className="rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none" />
        </div>
        {!data ? (
          <p className="text-sm text-mocha py-6 text-center">Memuat penyebaran stok…</p>
        ) : (
          <div className="space-y-2 mt-1">
            {Object.entries(data.sizes).map(([s, v]) => (
              <div key={s} data-testid={`inspect-size-${s.replace(/[^A-Za-z0-9]+/g, '-')}`} className="bg-white rounded-xl border border-border p-3">
                <div className="flex justify-between items-center">
                  <span className="font-display font-bold text-brown">{s}</span>
                  <span className={`text-xs font-bold ${v.available > 0 ? 'text-success' : 'text-danger'}`}>
                    tersedia {v.available}/{v.total}
                  </span>
                </div>
                {v.damaged > 0 && <p className="text-[11px] text-danger font-semibold mt-1">⚠ {v.damaged} pcs rusak/perbaikan</p>}
                {v.holders.length > 0 ? (
                  <div className="mt-1.5 space-y-0.5">
                    {v.holders.map((h, i) => (
                      <p key={i} data-testid={`inspect-holder-${s.replace(/[^A-Za-z0-9]+/g, '-')}-${i}`} className="text-[11px] text-mocha">
                        {STATE_L[h.state]} <b className="text-ink">{h.customer}</b> — {h.pcs} pcs <span className="text-brown">({h.ref})</span>{h.as_bawahan ? ' • sbg bawahan' : ''}
                      </p>
                    ))}
                  </div>
                ) : (
                  v.available === v.total - v.damaged && <p className="text-[11px] text-success mt-1">Semua pcs ready</p>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Stock() {
  const [products, setProducts] = useState(null);
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [view, setView] = useState('compact');
  const [hanyaRusak, setHanyaRusak] = useState(false);   // ketuk kartu Rusak / Perbaikan
  const [damagedFor, setDamagedFor] = useState(null);
  const [inspectFor, setInspectFor] = useState(null);
  const [damagedSizes, setDamagedSizes] = useState({});
  const [saving, setSaving] = useState(false);

  const start = todayStr();
  const end = shiftDateStr(start, 30);

  const load = useCallback(
    () =>
      api
        .get(`/products-availability?start=${start}&end=${end}`)
        .then((r) => setProducts(r.data))
        .catch(() => toast.error('Gagal memuat data stok')),
    [start, end]
  );

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () =>
      (products || []).filter(
        (p) =>
          (!category || p.category === category) &&
          (!hanyaRusak || p.total_damaged > 0) &&
          (p.name.toLowerCase().includes(q.toLowerCase()) || p.code.toLowerCase().includes(q.toLowerCase()))
      ),
    [products, category, q, hanyaRusak]
  );

  // Berapa barang (bukan pcs) yang sedang rusak/diperbaiki, tanpa terpengaruh kategori/cari.
  const jmlBarangRusak = useMemo(
    () => (products || []).filter((p) => p.total_damaged > 0).length,
    [products]
  );

  const totals = useMemo(() => {
    const t = { stock: 0, ready: 0, used: 0, damaged: 0 };
    (products || []).forEach((p) => {
      t.stock += p.total_stock;
      t.ready += p.available_total;
      t.used += p.used_total;
      t.damaged += p.total_damaged;
    });
    return t;
  }, [products]);

  const saveDamaged = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${damagedFor.id}`, {
        code: damagedFor.code, name: damagedFor.name, category: damagedFor.category,
        rental_price: damagedFor.rental_price, sizes: damagedFor.sizes,
        sizes_damaged: damagedSizes, default_bawahan_id: damagedFor.default_bawahan_id,
        custom_bawahan: damagedFor.custom_bawahan || '', set_contents: damagedFor.set_contents || '',
        photo: damagedFor.photo || '', notes: damagedFor.notes || '', status: damagedFor.status,
      });
      toast.success('Stok rusak/perbaikan diperbarui');
      setDamagedFor(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sizeBadges = (p, small = false) =>
    Object.entries(p.sizes).filter(([, v]) => v > 0).map(([s, v]) => {
      const avail = p.available_sizes?.[s] ?? 0;
      const dmg = p.sizes_damaged?.[s] || 0;
      return (
        <span
          key={s}
          data-testid={small ? undefined : `stock-size-${p.code}-${s.replace(/[^A-Z0-9]/gi, '')}`}
          className={`rounded-lg px-2 py-1 text-[10px] font-bold ${avail > 0 ? 'bg-brown/10 text-brown' : 'bg-neutral-100 text-neutral-400'}`}
          title={`stok ${v}${dmg ? `, rusak ${dmg}` : ''}, dipakai ${v - dmg - avail}`}
        >
          {s}: {avail}/{v}{dmg > 0 ? ` (${dmg} rusak)` : ''}
        </span>
      );
    });

  const damagedBtn = (p) => (
    <button
      data-testid={`stock-damaged-${p.code}`}
      onClick={() => {
        setDamagedFor(p);
        setDamagedSizes({ ...(p.sizes_damaged || {}) });
      }}
      className="inline-flex items-center gap-1.5 rounded-xl border border-border text-mocha px-3 py-1.5 text-[11px] font-semibold hover:bg-ivory transition-colors"
    >
      <Wrench size={12} /> Atur rusak / perbaikan
    </button>
  );

  return (
    <div className="space-y-5" data-testid="stock-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Stok</h1>
          <p className="text-xs text-mocha mt-1">Ketersediaan dihitung untuk 30 hari ke depan (sudah termasuk booking & sewa aktif).</p>
        </div>
        <div className="flex gap-1.5 bg-white border border-border rounded-xl p-1" data-testid="stock-view-toggle">
          {VIEWS.map((v) => (
            <button
              key={v.v}
              data-testid={`view-${v.v}`}
              onClick={() => setView(v.v)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === v.v ? 'bg-brown text-white' : 'text-mocha hover:bg-ivory'
              }`}
            >
              <v.icon size={14} /> {v.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stock-summary">
        {[
          { l: 'Total Stok', v: `${totals.stock} pcs`, t: 'text-ink' },
          { l: 'Ready', v: `${totals.ready} pcs`, t: 'text-success' },
          { l: 'Disewa / Dibooking', v: `${totals.used} pcs`, t: 'text-brown' },
          { l: 'Rusak / Perbaikan', v: `${totals.damaged} pcs`, t: 'text-danger' },
        ].map((s) => s.l === 'Rusak / Perbaikan' ? (
          <button
            key={s.l}
            type="button"
            data-testid="stock-card-rusak"
            aria-pressed={hanyaRusak}
            onClick={() => setHanyaRusak((v) => !v)}
            className={`text-left bg-white rounded-2xl border shadow-soft p-4 transition-colors hover:bg-ivory/60 ${hanyaRusak ? 'border-danger ring-2 ring-danger/30' : 'border-border'}`}
          >
            <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">{s.l}</p>
            <p className={`font-display text-xl md:text-2xl mt-1 ${s.t}`}>{s.v}</p>
            <p className="text-[10px] text-mocha mt-0.5">{hanyaRusak ? 'ketuk lagi untuk menutup daftar' : 'ketuk untuk lihat daftarnya'}</p>
          </button>
        ) : (
          <div key={s.l} className="bg-white rounded-2xl border border-border shadow-soft p-4">
            <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">{s.l}</p>
            <p className={`font-display text-xl md:text-2xl mt-1 ${s.t}`}>{s.v}</p>
          </div>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mocha" />
        <input
          data-testid="stock-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari kode atau nama barang…"
          className="w-full rounded-xl border border-border bg-white pl-10 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
        />
        <HapusCari show={!!q} onClear={() => setQ('')} testid="stock-search-clear" />
      </div>
      <div className="flex gap-1.5 flex-wrap" data-testid="stock-category-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c || 'all'}
            data-testid={`stock-filter-${(c || 'semua').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            onClick={() => setCategory(c)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              category === c ? 'bg-brown text-white' : 'bg-white border border-border text-mocha hover:bg-ivory'
            }`}
          >
            {c || 'Semua'}
          </button>
        ))}
      </div>

      {hanyaRusak && products && (
        <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="stock-rusak-banner">
          <p className="text-sm text-danger font-semibold">
            Menampilkan {jmlBarangRusak} barang yang rusak / dalam perbaikan ({totals.damaged} pcs)
          </p>
          <button type="button" data-testid="stock-rusak-clear" onClick={() => setHanyaRusak(false)}
            className="rounded-full bg-white border border-border text-mocha px-3 py-1 text-[11px] font-bold hover:bg-ivory">
            Tampilkan semua
          </button>
        </div>
      )}

      {!products ? (
        <p className="text-sm text-mocha py-10 text-center">Memuat stok…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-mocha py-10 text-center" data-testid="stock-empty">
          {hanyaRusak && jmlBarangRusak === 0 ? 'Tidak ada barang rusak atau dalam perbaikan. 👌' : 'Tidak ada produk.'}
        </p>
      ) : view === 'table' ? (
        <div className="bg-white rounded-2xl border border-border shadow-soft overflow-x-auto" data-testid="stock-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
                <th className="px-4 py-3 font-bold">Kode</th>
                <th className="px-4 py-3 font-bold">Nama</th>
                <th className="px-4 py-3 font-bold">Kategori</th>
                <th className="px-4 py-3 font-bold text-right">Harga</th>
                <th className="px-4 py-3 font-bold text-center">Stok</th>
                <th className="px-4 py-3 font-bold text-center">Ready</th>
                <th className="px-4 py-3 font-bold text-center">Dipakai</th>
                <th className="px-4 py-3 font-bold">Per Ukuran</th>
                <th className="px-4 py-3 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`stock-row-${p.code}`} className="border-b border-border/60 hover:bg-ivory/60">
                  <td className="px-4 py-3 font-bold text-brown">{p.code}</td>
                  <td className="px-4 py-3 font-semibold">{p.name}</td>
                  <td className="px-4 py-3 text-mocha">{p.category}</td>
                  <td className="px-4 py-3 text-right">{formatIDR(p.rental_price)}</td>
                  <td className="px-4 py-3 text-center">{p.total_stock}</td>
                  <td className="px-4 py-3 text-center font-bold text-success">{p.available_total}</td>
                  <td className="px-4 py-3 text-center text-brown">{p.used_total}</td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{sizeBadges(p, true)}</div></td>
                  <td className="px-4 py-3">{damagedBtn(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : view === 'compact' ? (
        <div className="space-y-2" data-testid="stock-compact">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`stock-card-${p.code}`} className="bg-white rounded-xl border border-border shadow-soft px-4 py-3 flex flex-wrap items-center gap-3">
              <span className="font-display font-bold text-brown text-sm w-16">{p.code}</span>
              <span className="text-sm font-semibold text-ink flex-1 min-w-[140px] truncate">{p.name}</span>
              <span className="text-xs text-mocha">{formatIDR(p.rental_price)}</span>
              <div className="flex flex-wrap gap-1">{sizeBadges(p, true)}</div>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${p.available_total > 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                ready {p.available_total}
              </span>
              <button
                data-testid={`stock-inspect-${p.code}`}
                onClick={() => setInspectFor(p)}
                className="p-2 rounded-lg text-brown hover:bg-brown/10"
                title="Lihat siapa yang memegang stok (disewa/dibooking/laundry)"
              >
                <Eye size={14} />
              </button>
              {damagedBtn(p)}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-testid="stock-grid">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`stock-card-${p.code}`} className="bg-white rounded-2xl border border-border shadow-soft overflow-hidden card-lift">
              <div className="flex gap-3 p-4">
                <img src={p.photo} alt={p.name} className="w-16 h-16 rounded-xl object-cover bg-muted shrink-0" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display font-bold text-brown text-sm">{p.code}</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${p.available_total > 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                      {p.available_total > 0 ? `ready ${p.available_total}` : 'habis'}
                    </span>
                    <button
                      data-testid={`stock-inspect-${p.code}`}
                      onClick={() => setInspectFor(p)}
                      className="p-1.5 rounded-lg text-brown hover:bg-brown/10"
                      title="Lihat siapa yang memegang stok"
                    >
                      <Eye size={13} />
                    </button>
                  </div>
                  <p className="text-sm font-semibold text-ink mt-0.5 truncate">{p.name}</p>
                  <p className="text-xs text-mocha">{p.category} • {formatIDR(p.rental_price)}/pcs • stok {p.total_stock}</p>
                </div>
              </div>
              <div className="px-4 pb-3 flex flex-wrap gap-1.5">{sizeBadges(p)}</div>
              <div className="px-4 pb-4">{damagedBtn(p)}</div>
            </div>
          ))}
        </div>
      )}

      <InspectDialog product={inspectFor} onClose={() => setInspectFor(null)} />

      <Dialog open={!!damagedFor} onOpenChange={() => setDamagedFor(null)}>
        <DialogContent className="max-w-md" data-testid="damaged-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-brown-dark text-xl">Rusak / Perbaikan — {damagedFor?.code}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-mocha">Jumlah pcs yang tidak bisa disewakan per ukuran. Otomatis mengurangi stok ready.</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {damagedFor && Object.entries(damagedFor.sizes).filter(([, v]) => v > 0).map(([s, v]) => {
              const val = damagedSizes[s] || 0;
              return (
                <div key={s} className="rounded-xl border border-border bg-ivory/50 px-3 py-2 text-center">
                  <p className="text-[10px] font-bold text-mocha">{s} (stok {v})</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <button data-testid={`damaged-${s.replace(/[^A-Z0-9]/gi, '')}-minus`} disabled={val <= 0} onClick={() => setDamagedSizes({ ...damagedSizes, [s]: Math.max(0, val - 1) })} className="w-7 h-7 rounded-lg border border-border bg-white text-brown flex items-center justify-center disabled:opacity-30">
                      <Minus size={13} />
                    </button>
                    <span className="w-6 text-center text-sm font-bold">{val}</span>
                    <button data-testid={`damaged-${s.replace(/[^A-Z0-9]/gi, '')}-plus`} disabled={val >= v} onClick={() => setDamagedSizes({ ...damagedSizes, [s]: Math.min(v, val + 1) })} className="w-7 h-7 rounded-lg border border-border bg-white text-brown flex items-center justify-center disabled:opacity-30">
                      <Plus size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <button data-testid="save-damaged-button" onClick={saveDamaged} disabled={saving} className="mt-4 w-full rounded-xl bg-brown text-white py-3 font-semibold text-sm hover:bg-brown-med transition-colors disabled:opacity-60">
            {saving ? 'Menyimpan…' : 'Simpan'}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
